import React, { useCallback, useContext, useEffect, useState } from "react";
import { Pagination } from "../utils/Pagination";
import { fetchOrderedCourses } from "../services/admin/fetchOrderedCourses";
import { DContext } from "../context/Datacontext";
import { SmartFilterBar } from "../components/common/SmartFilterBar";
import { updateOrderAccess } from "../services/admin/updateOrderAccess "; // <--- removed trailing space
import { useNavigate } from "react-router-dom";
import { GrView } from "react-icons/gr";
import { MdOutlineDelete } from "react-icons/md";

export const Orders = () => {
    const { BeURL } = useContext(DContext);
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    const [accessModal, setAccessModal] = useState(null);
    const [expiryType, setExpiryType] = useState("lifetime");
    const [customExpiryDate, setCustomExpiryDate] = useState(""); // yyyy-mm-dd
    const [paymentStatus, setPaymentStatus] = useState("paid"); // 'paid' or 'unpaid'
    const [rejectionReason, setRejectionReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);


    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetchOrderedCourses({
                BeURL,
                page,
                limit,
                ...filters,
            });

            if (res?.success) {
                setOrders(res.orders || []);
                setTotalPages(res.totalPages || 1);
                setTotal(res.total || 0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [BeURL, limit, filters, page]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const isFutureDate = (d) => {
        if (!d) return false;
        const selected = new Date(d);
        const now = new Date();
        // Set time of 'now' to 00:00 if you want date-only check
        return selected.getTime() > now.getTime();
    };

    const computeExpiryIso = (type) => {
        if (type === "lifetime") return null;
        const now = new Date();
        if (type === "7days") {
            now.setDate(now.getDate() + 7);
            return now.toISOString();
        }
        if (type === "30days") {
            now.setDate(now.getDate() + 30);
            return now.toISOString();
        }
        if (type === "90days") {
            now.setDate(now.getDate() + 90);
            return now.toISOString();
        }
        if (type === "custom" && customExpiryDate) {
            const d = new Date(customExpiryDate);
            return d.toISOString();
        }
        return null;
    };

    /* ================= GRANT / UPDATE ACCESS ================= */
    const handleGrantAccess = async () => {
        if (!accessModal) return;

        // validations
        if (expiryType === "custom" && !isFutureDate(customExpiryDate)) {
            alert("Please choose a valid future date for custom expiry.");
            return;
        }
        if (paymentStatus === "unpaid" && rejectionReason.trim().length === 0) {
            alert("Please enter a reason for marking unpaid / revoking access.");
            return;
        }

        setIsSubmitting(true);
        try {
            const expiresAtIso = computeExpiryIso(expiryType); // null for lifetime

            // build payload — keep shape simple and let backend interpret 'expiresAt' null as lifetime
            const payload = {
                BeURL,
                id: accessModal._id,
                accessGranted: paymentStatus === "paid",
                expiresAt: expiresAtIso, // ISO or null
                paymentStatus, // 'paid' or 'unpaid'
                rejectionReason: paymentStatus === "unpaid" ? rejectionReason.trim() : undefined,
            };

            const res = await updateOrderAccess(payload);

            if (res?.success) {
                // update local orders state (no reload)
                setOrders((prev) =>
                    prev.map((o) =>
                        o._id === accessModal._id
                            ? {
                                ...o,
                                accessGranted: payload.accessGranted,
                                expiresAt: payload.expiresAt,
                                accessGrantedAt: payload.accessGranted ? new Date().toISOString() : o.accessGrantedAt,
                                orderStatus: payload.accessGranted ? "completed" : "created",
                            }
                            : o
                    )
                );

                setAccessModal(null);
                // reset modal fields
                setExpiryType("lifetime");
                setCustomExpiryDate("");
                setPaymentStatus("paid");
                setRejectionReason("");
            } else {
                console.error("updateOrderAccess failed", res);
                alert(res?.message || "Failed to update order access");
            }
        } catch (err) {
            console.error(err);
            alert("Something went wrong. Check console.");
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ================= DELETE ORDER ================= */
    const handleDeleteOrder = async (id) => {
        try {
            const res = await fetch(`${BeURL}/delete-order/${id}`, {
                method: "DELETE",
                credentials: "include",
            });

            const data = await res.json();

            if (data.success) {
                setOrders((prev) => prev.filter((o) => o._id !== id));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleCourse = (courseId) => {
        navigate("/app/course-view", {
            state: { courseId },
        });
    };

    const handleUser = (userId) => {
        navigate("/app/view-user", { state: { userId } });
    };

    return (
        <div className="p-8 space-y-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex justify-between">
                <h2 className="text-2xl font-bold">Orders</h2>
                <span className="bg-primary-100 text-primary-700 text-xs px-3 py-1 rounded-full">
                    Total: {total}
                </span>
            </div>

            {/* FILTER BAR */}
            <SmartFilterBar
                BeURL={BeURL}
                onChange={(f) => {
                    setFilters(f);
                    setPage(1);
                }}
            />

            {/* Table */}
            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-primary-100 text-xs capitalize text-primary-600">
                        <tr>
                            <th className="p-3 border">Sl.No</th>
                            <th className="p-3 border">Order ID</th>
                            <th className="p-3 border">Student</th>
                            <th className="p-3 border">Course</th>
                            <th className="p-3 border">Amount</th>
                            <th className="p-3 border">Payment</th>
                            <th className="p-3 border">Access Granted At</th>
                            <th className="p-3 border">Expires At</th>
                            <th className="p-3 border">Status</th>
                            <th className="p-3 border">Action</th>
                            <th className="p-3 border">Access</th>
                        </tr>
                    </thead>

                    <tbody>
                        {orders.map((o, i) => {
                            const statusLower = (o.orderStatus || "").toLowerCase();
                            return (
                                <tr key={o._id}>
                                    <td className="p-3 border text-center">{i + 1}</td>
                                    <td className="p-3 border text-center">{o.orderId}</td>

                                    <td
                                        className="p-3 border text-center font-semibold text-primary-600 cursor-pointer hover:underline"
                                        onClick={() => handleUser(o?.userId)}
                                    >
                                        {o.userFullName}
                                    </td>

                                    <td
                                        className="p-3 border text-center font-semibold text-primary-600 cursor-pointer hover:underline"
                                        onClick={() => handleCourse(o?.course?.id)}
                                    >
                                        {o.course?.title}
                                    </td>

                                    <td className="p-3 border text-center font-semibold">
                                        ₹{o.pricing?.totalAmount}
                                    </td>

                                    <td className="p-3 border text-center">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-semibold ${["expired", "completed"].includes(statusLower)
                                                ? "bg-green-100 text-green-700"
                                                : "bg-yellow-100 text-yellow-700"
                                                }`}
                                        >
                                            {["expired", "completed"].includes(statusLower) ? "🟢 Paid" : "🟡 Pending"}
                                        </span>
                                    </td>

                                    <td className="p-3 border text-center font-semibold">
                                        {o?.accessGrantedAt ? new Date(o.accessGrantedAt).toLocaleDateString() : "-"}
                                    </td>

                                    <td className="p-3 border text-center font-semibold">
                                        {o?.expiresAt ? new Date(o.expiresAt).toLocaleDateString() : "-"}
                                    </td>

                                    <td className="p-3 border text-center">{o.orderStatus}</td>

                                    <td className="p-3 border text-center flex gap-2 justify-center">
                                        <button
                                            title="View"
                                            onClick={() => setSelectedOrder(o)}
                                            className="text-primary-600 hover:text-primary-700 rounded-lg text-xs"
                                        >
                                            <GrView size={20} />
                                        </button>

                                        <button
                                            title="Delete"
                                            onClick={() => handleDeleteOrder(o._id)}
                                            className=" text-red-500 hover:text-red-600 rounded-lg text-xs"
                                        >
                                            <MdOutlineDelete size={20} />
                                        </button>
                                    </td>

                                    {/* ACCESS BUTTON */}
                                    {["completed"].includes(statusLower) ? (
                                        <td className="p-3 border text-center">
                                            <button
                                                onClick={() => {
                                                    setAccessModal(o);
                                                    setExpiryType("lifetime");
                                                    setCustomExpiryDate("");
                                                    setPaymentStatus("paid");
                                                    setRejectionReason("");
                                                }}
                                                className={`w-12 h-6 rounded-full flex items-center transition-all ${["completed"].includes(statusLower)
                                                    ? "bg-green-500 justify-end"
                                                    : "bg-gray-300 justify-start hover:bg-gray-400"
                                                    }`}
                                            >
                                                <span className="w-5 h-5 bg-white rounded-full shadow" />
                                            </button>
                                        </td>
                                    ) : ["created", "expired"].includes(statusLower) ? (
                                        <td className="p-3 border text-center">
                                            <button
                                                onClick={() => {
                                                    setAccessModal(o);
                                                    setExpiryType("lifetime");
                                                    setCustomExpiryDate("");
                                                    setPaymentStatus("paid");
                                                    setRejectionReason("");
                                                }}
                                                className={`w-12 h-6 rounded-full flex items-center transition-all ${["created", "expired"].includes(statusLower)
                                                    ? "bg-gray-300 justify-start hover:bg-gray-400"
                                                    : "bg-green-500 justify-end"
                                                    }`}
                                            >
                                                <span className="w-5 h-5 bg-white rounded-full shadow" />
                                            </button>
                                        </td>
                                    ) : (
                                        <td className="p-3 border text-center">-</td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <Pagination
                page={page}
                totalPages={totalPages}
                limit={limit}
                totalUsers={total}
                setPage={setPage}
                setLimit={setLimit}
            />

            {/* ORDER DETAILS POPUP */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-primary-50/10 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-8 space-y-6">
                        {/* header */}
                        <div className="flex justify-between items-start border-b pb-4">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">Order Details</h3>
                                <p className="text-sm text-gray-500">Complete order information</p>
                            </div>
                            <button onClick={() => setSelectedOrder(null)} className="text-sm">
                                ✕
                            </button>
                        </div>

                        {/* basic info */}
                        <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-gray-500">Order ID</p>
                                <p className="font-semibold text-gray-800">{selectedOrder.orderId}</p>
                            </div>

                            <div>
                                <p className="text-gray-500">Order Status</p>
                                <span
                                    className={`px-2 py-1 text-xs rounded-full font-medium ${selectedOrder.orderStatus === "completed" ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"
                                        }`}
                                >
                                    {selectedOrder.orderStatus}
                                </span>
                            </div>

                            <div>
                                <p className="text-gray-500">Student</p>
                                <p className="font-semibold text-gray-800">{selectedOrder.userFullName}</p>
                            </div>

                            <div>
                                <p className="text-gray-500">Email</p>
                                <p className="font-semibold text-gray-800">{selectedOrder.email}</p>
                            </div>

                            <div>
                                <p className="text-gray-500">Payment</p>
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${selectedOrder.accessGranted ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                                    {selectedOrder.accessGranted ? "paid" : "pending"}
                                </span>
                            </div>

                            <div>
                                <p className="text-gray-500">Purchased On</p>
                                <p className="font-semibold text-gray-800">{new Date(selectedOrder.createdAt).toLocaleDateString()}</p>
                            </div>
                        </div>

                        {/* course info */}
                        <div className="space-y-3">
                            <h4 className="text-lg font-semibold text-gray-800">Course</h4>

                            <div className="flex items-center gap-4 border rounded-xl p-4 hover:shadow-md transition">
                                <img src={selectedOrder.course?.thumbnail} alt="" className="w-24 h-16 object-cover rounded-lg" />

                                <div className="flex-1">
                                    <p className="font-semibold text-gray-800">{selectedOrder.course?.title}</p>
                                    <p className="text-xs text-gray-400 mt-1">Code: {selectedOrder.courseDoc?.code}</p>
                                </div>

                                <div className="text-lg font-bold text-indigo-600">₹{selectedOrder.pricing?.totalAmount}</div>
                            </div>
                        </div>

                        {/* pricing */}
                        <div className="bg-indigo-50 rounded-2xl p-5 space-y-2 border text-sm">
                            <div className="flex justify-between">
                                <span>Subtotal</span>
                                <span>₹{selectedOrder.pricing?.subtotal}</span>
                            </div>

                            <div className="flex justify-between text-green-600">
                                <span>Discount</span>
                                <span>- ₹{selectedOrder.pricing?.discount}</span>
                            </div>

                            <div className="border-t pt-3 flex justify-between font-bold text-gray-900 text-base">
                                <span>Total Paid</span>
                                <span>₹{selectedOrder.pricing?.totalAmount}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ACCESS MODAL */}
            {accessModal && (
                <div
                    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
                    onClick={(e) => e.target === e.currentTarget && setAccessModal(null)}
                >
                    <div className="bg-white w-[420px] rounded-xl p-6 space-y-4" role="dialog" aria-modal="true">
                        <div className="flex justify-between">
                            <h3 className="font-semibold text-lg">Grant Course Access</h3>
                            <button onClick={() => setAccessModal(null)}>✕</button>
                        </div>

                        {["created", "expired"].includes((accessModal.orderStatus || "").toLowerCase()) ? (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600">
                                    This order is currently pending. You can grant course access to the student. Access will be active immediately and will expire based on the selected duration.
                                </p>

                                <select value={expiryType} onChange={(e) => setExpiryType(e.target.value)} className="w-full border rounded-lg p-2">
                                    <option value="lifetime">Lifetime Access</option>
                                    <option value="7days">7 Days</option>
                                    <option value="30days">30 Days</option>
                                    <option value="90days">90 Days</option>
                                    <option value="custom">Custom Date</option>
                                </select>

                                {expiryType === "custom" && (
                                    <input type="date" value={customExpiryDate} onChange={(e) => setCustomExpiryDate(e.target.value)} className="w-full border rounded-lg p-2" />
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600">
                                    This order already has access granted. You can update the expiry date by selecting a new duration. The new expiry will override the previous one.
                                </p>

                                <div className="flex gap-2">
                                    <button type="button" className={`px-3 py-1 rounded ${paymentStatus === "paid" ? "bg-green-100" : "bg-gray-100"}`} onClick={() => setPaymentStatus("paid")}>
                                        Paid
                                    </button>
                                    <button type="button" className={`px-3 py-1 rounded ${paymentStatus === "unpaid" ? "bg-red-100" : "bg-gray-100"}`} onClick={() => setPaymentStatus("unpaid")}>
                                        Unpaid
                                    </button>
                                </div>

                                {paymentStatus === "paid" && (
                                    <div className="space-y-2 mt-2">
                                        <p className="text-sm text-gray-600">The access will be active immediately and will expire based on the selected duration.</p>

                                        <select value={expiryType} onChange={(e) => setExpiryType(e.target.value)} className="w-full border rounded-lg p-2">
                                            <option value="lifetime">Lifetime Access</option>
                                            <option value="7days">7 Days</option>
                                            <option value="30days">30 Days</option>
                                            <option value="90days">90 Days</option>
                                            <option value="custom">Custom Date</option>
                                        </select>

                                        {expiryType === "custom" && (
                                            <input type="date" value={customExpiryDate} onChange={(e) => setCustomExpiryDate(e.target.value)} className="w-full border rounded-lg p-2" />
                                        )}
                                    </div>
                                )}

                                {paymentStatus === "unpaid" && (
                                    <div className="space-y-2 mt-2">
                                        <p className="text-sm text-gray-600">The access will be revoked immediately and order status will be changed to pending. You can grant access again whenever needed.</p>

                                        <input type="text" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Reason" className="w-full border rounded-lg p-2" />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setAccessModal(null)} className="px-4 py-1 bg-gray-200 rounded-lg" disabled={isSubmitting}>
                                Cancel
                            </button>

                            <button
                                onClick={handleGrantAccess}
                                className="px-4 py-1 bg-primary-600 text-white rounded-lg"
                                disabled={
                                    isSubmitting ||
                                    (expiryType === "custom" && !isFutureDate(customExpiryDate)) ||
                                    (paymentStatus === "unpaid" && rejectionReason.trim().length === 0)
                                }
                            >
                                {isSubmitting ? "Processing..." : "Grant Access"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};